import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixIdentityInvitesV1SiteInvite from './identity-invites-v1-site-invite-site-invites.http.js';

export interface SiteInvite {
  /**
   * Invite ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Site ID the user is invited to as a collaborator.
   * @format GUID
   * @readonly
   */
  siteId?: string;
  /**
   * Email address where the invite was sent.
   * @format EMAIL
   */
  email?: string;
  /** Role IDs included in the invite. */
  policyIds?: string[];
  /**
   * Deprecated. Use `inviterAccountId`.
   * @readonly
   * @deprecated
   */
  inviterId?: string;
  /**
   * Invite Status.
   *
   * Supported values:
   * - **Pending:** The invite has been sent and is valid, waiting for the user's response.
   * - **Used:** The invite has been accepted.
   * - **Deleted:** The invite has been deleted or revoked.
   * - **Declined:** The user declined the invite.
   * - **Expired:** The invite has expired without being accepted.
   */
  status?: InviteStatusWithLiterals;
  /** Link to accept the invite. */
  acceptLink?: string;
  /**
   * Inviting account ID.
   * @format GUID
   * @readonly
   */
  inviterAccountId?: string;
  /**
   * Account ID that accepted the invite. Populated only once the invite is accepted.
   * @format GUID
   * @readonly
   */
  acceptedByAccountId?: string | null;
  /** Date the invite was created. */
  dateCreated?: Date | null;
  /**
   * User's Wix Bookings staff ID, if relevant.
   * @format GUID
   */
  staffId?: string | null;
  /** Invite expiration date */
  expirationDate?: Date | null;
  /**
   * Location ids included in the invite
   * @format GUID
   * @readonly
   * @maxSize 20
   */
  locationIds?: string[];
  /**
   * A set of capability keys representing the actions that the caller is allowed
   * to perform on this specific contributor. This field is calculated based on the
   * identity of the request sender and may differ between callers.
   * @maxSize 20
   * @maxLength 20
   */
  callerCapabilities?: string[];
}

/** Invite status stating whether the invite was accepted, waiting to be accepted, deleted etc.. */
export enum InviteStatus {
  Pending = 'Pending',
  Used = 'Used',
  Deleted = 'Deleted',
  Declined = 'Declined',
  Expired = 'Expired',
}

/** @enumType */
export type InviteStatusWithLiterals =
  | InviteStatus
  | 'Pending'
  | 'Used'
  | 'Deleted'
  | 'Declined'
  | 'Expired';

export interface GetSiteInvitesRequest {}

export interface GetSiteInvitesResponse {
  invites?: SiteInvite[];
}

export interface QuerySiteInvitesRequest {
  /**
   * Supports only `filter` field with
   * `"filter" : {
   * "acceptedByAccountId":{"$in": [<id1>, <id2>, ...]}
   * }`
   */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QuerySiteInvitesResponse {
  invites?: SiteInvite[];
}

export interface GetSiteInviteRequest {
  _id?: string;
}

export interface GetSiteInviteResponse {
  invite?: SiteInvite;
}

export interface SiteInviteRequest {
  /** The role ids to be assigned */
  policyIds?: string[];
  /**
   * Invitee email
   * @format EMAIL
   */
  email?: string;
  /** The language of emails that will be used only for recipients that don't have a user, in case this parameter is unspecified, the sender's language will be used instead */
  defaultEmailLanguage?: string | null;
  /** Location restriction for the invite. */
  locationRestriction?: LocationRestriction;
}

export interface LocationRestriction {
  /**
   * @format GUID
   * @maxSize 20
   */
  locationIds?: string[];
}

export interface SiteInviteResponse {
  /** Invites that were sent. */
  invite?: SiteInvite;
}

export interface BulkSiteInviteRequest {
  /** Role IDs, referred to as policy IDs, to assign to the contributors. */
  policyIds: string[];
  /**
   * Email addresses to which the invites should be sent.
   * @minSize 1
   * @maxSize 10
   */
  emails: string[];
  /** Details explaining the purpose of the invite. */
  invitePurpose?: string | null;
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
  /** Location restriction for the invite. */
  locationRestriction?: LocationRestriction;
}

export interface BulkSiteInviteResponse {
  /** Invites that were sent successfully. */
  invites?: SiteInvite[];
  /** Invites that failed. */
  failedEmails?: string[];
}

export interface ResendSiteInviteRequest {
  /** Invite ID. */
  inviteId: string;
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
}

export interface AcceptSiteInviteRequest {
  inviteToken?: string;
}

export interface AcceptSiteInviteResponse {}

export interface RevokeSiteInviteRequest {
  /** Invite ID. */
  inviteId: string;
}

export interface RevokeSiteInviteResponse {}

export interface UpdateSiteInviteRequest {
  inviteId?: string;
  policyIds?: string[];
  /** @format GUID */
  staffId?: string | null;
  locationRestriction?: LocationRestriction;
}

export interface UpdateSiteInviteResponse {}

export interface GetContributorLimitRequest {}

export interface GetContributorLimitResponse {
  contributorLimitation?: ContributorLimitation;
}

export interface ContributorLimitation {
  contributorLimit?: number;
  leftInvites?: number;
}

export interface ParseSiteInviteTokenRequest {
  inviteToken?: string;
}

export interface ParseSiteInviteTokenResponse {
  inviteId?: string;
  siteId?: string;
  status?: InviteStatusWithLiterals;
}

/**
 * Creates and sends emails inviting potential site contributors to become contributors in the requesting site.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 * @param policyIds - Role IDs, referred to as policy IDs, to assign to the contributors.
 * @public
 * @requiredField options.emails
 * @requiredField policyIds
 * @param options - Filter options. The `emails` field **must** be passed.
 * @permissionId site-users.send-invite
 * @applicableIdentity APP
 * @fqn com.wixpress.invites.api.SiteInvitesApi.BulkInvite
 */
export async function bulkInvite(
  policyIds: string[],
  options?: NonNullablePaths<BulkInviteOptions, `emails`, 2>
): Promise<
  NonNullablePaths<
    BulkSiteInviteResponse,
    | `invites`
    | `invites.${number}._id`
    | `invites.${number}.siteId`
    | `invites.${number}.email`
    | `invites.${number}.inviterId`
    | `invites.${number}.status`
    | `invites.${number}.acceptLink`
    | `invites.${number}.inviterAccountId`
    | `failedEmails`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    policyIds: policyIds,
    emails: options?.emails,
    invitePurpose: options?.invitePurpose,
    defaultEmailLanguage: options?.defaultEmailLanguage,
    locationRestriction: options?.locationRestriction,
  });

  const reqOpts = ambassadorWixIdentityInvitesV1SiteInvite.bulkInvite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          policyIds: '$[0]',
          emails: '$[1].emails',
          invitePurpose: '$[1].invitePurpose',
          defaultEmailLanguage: '$[1].defaultEmailLanguage',
          locationRestriction: '$[1].locationRestriction',
        },
        singleArgumentUnchanged: false,
      },
      ['policyIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkInviteOptions {
  /**
   * Email addresses to which the invites should be sent.
   * @minSize 1
   * @maxSize 10
   */
  emails: string[];
  /** Details explaining the purpose of the invite. */
  invitePurpose?: string | null;
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
  /** Location restriction for the invite. */
  locationRestriction?: LocationRestriction;
}

/**
 * Resends the email invitation to a potential site contributor.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 * @param inviteId - Invite ID.
 * @public
 * @requiredField inviteId
 * @param options - Filter options.
 * @permissionId site-users.send-invite
 * @applicableIdentity APP
 * @fqn com.wixpress.invites.api.SiteInvitesApi.ResendInvite
 */
export async function resendInvite(
  inviteId: string,
  options?: ResendInviteOptions
): Promise<
  NonNullablePaths<
    SiteInviteResponse,
    | `invite._id`
    | `invite.siteId`
    | `invite.email`
    | `invite.policyIds`
    | `invite.inviterId`
    | `invite.status`
    | `invite.acceptLink`
    | `invite.inviterAccountId`
    | `invite.locationIds`
    | `invite.callerCapabilities`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inviteId: inviteId,
    defaultEmailLanguage: options?.defaultEmailLanguage,
  });

  const reqOpts =
    ambassadorWixIdentityInvitesV1SiteInvite.resendInvite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          inviteId: '$[0]',
          defaultEmailLanguage: '$[1].defaultEmailLanguage',
        },
        singleArgumentUnchanged: false,
      },
      ['inviteId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ResendInviteOptions {
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
}

/**
 * Revokes a pending site contributor invite.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 * @param inviteId - Invite ID.
 * @public
 * @requiredField inviteId
 * @permissionId site-users.delete-invite
 * @applicableIdentity APP
 * @fqn com.wixpress.invites.api.SiteInvitesApi.RevokeInvite
 */
export async function revokeInvite(inviteId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ inviteId: inviteId });

  const reqOpts =
    ambassadorWixIdentityInvitesV1SiteInvite.revokeInvite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { inviteId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['inviteId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
